import React, { useState } from 'react';
import { ArrowLeft, User, Building, QrCode, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { mockBanks, formatCurrency } from '@/utils/mockData';
import { toast } from 'sonner';

export default function TransferPage() {
  const [amount, setAmount] = useState('');
  const [transferType, setTransferType] = useState('wallet');

  const handleTransfer = (e) => {
    e.preventDefault();
    toast.success(`Transfer of ${formatCurrency(parseFloat(amount))} successful!`);
    setAmount('');
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="transfer-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold font-heading text-slate-900">Send Money</h1>
        <p className="text-slate-600 mt-1">Transfer to Britedge wallets, bank accounts, or phone numbers</p>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <Tabs defaultValue="wallet" onValueChange={setTransferType}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="wallet" data-testid="transfer-tab-wallet">
              <User className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Wallet</span>
            </TabsTrigger>
            <TabsTrigger value="bank" data-testid="transfer-tab-bank">
              <Building className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Bank</span>
            </TabsTrigger>
            <TabsTrigger value="qr" data-testid="transfer-tab-qr">
              <QrCode className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">QR Code</span>
            </TabsTrigger>
            <TabsTrigger value="phone" data-testid="transfer-tab-phone">
              <Phone className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Phone</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="wallet" className="space-y-4">
            <form onSubmit={handleTransfer} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wallet-id">Wallet ID or Username</Label>
                <Input 
                  id="wallet-id" 
                  placeholder="@username or wallet ID"
                  data-testid="wallet-id-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount (₦)</Label>
                <Input 
                  id="amount" 
                  type="number" 
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="transfer-amount-input"
                  required
                  min="100"
                  className="h-12 rounded-lg text-2xl font-bold"
                />
                <p className="text-sm text-slate-600">Min: ₦100 • Fee: ₦0</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Input 
                  id="description" 
                  placeholder="What's this for?"
                  data-testid="transfer-description-input"
                  className="h-12 rounded-lg"
                />
              </div>

              <Button 
                type="submit"
                data-testid="transfer-submit-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Send {amount && formatCurrency(parseFloat(amount))}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="bank" className="space-y-4">
            <form onSubmit={handleTransfer} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bank-name">Select Bank</Label>
                <Select required>
                  <SelectTrigger className="h-12 rounded-lg" data-testid="bank-select">
                    <SelectValue placeholder="Choose a bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockBanks.map((bank) => (
                      <SelectItem key={bank} value={bank.toLowerCase().replace(' ', '-')}>
                        {bank}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-number">Account Number</Label>
                <Input 
                  id="account-number" 
                  type="text"
                  placeholder="1234567890"
                  data-testid="account-number-input"
                  required
                  maxLength="10"
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bank-amount">Amount (₦)</Label>
                <Input 
                  id="bank-amount" 
                  type="number" 
                  placeholder="0.00"
                  data-testid="bank-amount-input"
                  required
                  min="100"
                  className="h-12 rounded-lg text-2xl font-bold"
                />
                <p className="text-sm text-slate-600">Min: ₦100 • Fee: ₦25</p>
              </div>

              <Button 
                type="submit"
                data-testid="bank-transfer-submit-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Transfer to Bank
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="qr" className="space-y-4">
            <div className="text-center py-12">
              <div className="w-48 h-48 mx-auto bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
                <QrCode className="w-24 h-24 text-slate-400" />
              </div>
              <p className="text-slate-600">Scan recipient's QR code to transfer</p>
              <Button 
                data-testid="scan-qr-btn"
                className="mt-4 bg-brand-blue text-white hover:bg-blue-700 rounded-xl px-6 py-3 transition-all active:scale-95"
              >
                Open Camera
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="phone" className="space-y-4">
            <form onSubmit={handleTransfer} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone-number">Phone Number</Label>
                <Input 
                  id="phone-number" 
                  type="tel"
                  placeholder="+234 801 234 5678"
                  data-testid="phone-number-input"
                  required
                  className="h-12 rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone-amount">Amount (₦)</Label>
                <Input 
                  id="phone-amount" 
                  type="number" 
                  placeholder="0.00"
                  data-testid="phone-amount-input"
                  required
                  min="100"
                  className="h-12 rounded-lg text-2xl font-bold"
                />
                <p className="text-sm text-slate-600">Min: ₦100 • Fee: ₦0</p>
              </div>

              <Button 
                type="submit"
                data-testid="phone-transfer-submit-btn"
                className="w-full bg-brand-blue text-white hover:bg-blue-700 rounded-xl h-12 text-lg font-semibold shadow-lg hover:shadow-blue-500/25 transition-all active:scale-95"
              >
                Send to Phone
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>

      {/* Recent Recipients */}
      <div className="mt-6 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="font-bold font-heading text-slate-900 mb-4">Recent Recipients</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['Chioma Obi', 'Tunde Bakare', 'Amina Hassan', 'Emeka Nwosu'].map((name, idx) => (
            <button 
              key={idx}
              data-testid={`recent-recipient-${idx}`}
              className="p-4 rounded-xl border border-slate-200 hover:border-brand-blue hover:bg-blue-50 transition-all"
            >
              <div className="w-12 h-12 rounded-full bg-slate-200 mx-auto mb-2"></div>
              <p className="text-sm font-medium text-slate-900 text-center">{name}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
